import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { User, Upload, FileText, Calendar, BookOpen, LogOut, HelpCircle, Package, ClipboardCheck, LayoutDashboard, X, Download, List } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DashboardSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();

  const mainItems = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Service Selection",
      url: "/service-selection",
      icon: List,
    },
    {
      title: "Your Package",
      url: "/your-package",
      icon: Package,
    },
    {
      title: "Personal Information",
      url: "/personal-info",
      icon: User,
    },
    {
      title: "Document Upload",
      url: "/documents",
      icon: Upload,
    },
    {
      title: "Application Form",
      url: "/application-form",
      icon: FileText,
    },
    {
      title: "Review Application",
      url: "/review-application",
      icon: ClipboardCheck,
    },
    {
      title: "Schedule Appointment",
      url: "/appointment",
      icon: Calendar,
    }
  ];

  const bottomItems = [
    {
      title: "Resources",
      url: "/resources",
      icon: Download,
    },
    {
      title: "Knowledge Base",
      url: "/knowledge-base",
      icon: BookOpen,
    },
    {
      title: "Help Center",
      url: "/help-center",
      icon: HelpCircle,
    },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleNavigate = (url: string) => {
    navigate(url);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar className="pt-[4.2rem]">
      <SidebarContent className="px-2">
        {isMobile && (
          <div className="flex justify-end p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenMobile(false)}
              className="hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}
        <SidebarGroup>
          <SidebarGroupLabel className="px-4 text-gray-500 text-sm">Application Process</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item, index) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.url;
                return (
                  <SidebarMenuItem 
                    key={item.title} 
                    onClick={() => handleNavigate(item.url)}
                  >
                    <SidebarMenuButton asChild isActive={isActive}>
                      <div className="flex items-center space-x-3 px-3 py-2 rounded-md transition-colors hover:bg-gray-100">
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="p-4 space-y-2">
        <SidebarMenu>
          {bottomItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <SidebarMenuItem 
                key={item.title} 
                onClick={() => handleNavigate(item.url)}
              >
                <SidebarMenuButton asChild>
                  <div className="flex items-center space-x-3 px-3 py-2 rounded-md transition-colors hover:bg-gray-100">
                    <IconComponent className="h-4 w-4" />
                    <span className="text-sm font-medium">{item.title}</span>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
        
        <Button 
          variant="outline" 
          onClick={handleLogout}
          className="w-full justify-start transition-colors hover:bg-red-50 text-red-600 hover:text-red-700"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;
